import { Link } from 'react-router-dom';
import {
  ArrowUpRight, ArrowRight, AlertTriangle, Sparkles, Camera, FileText, TrendingUp,
} from 'lucide-react';
import {
  AreaChart, Area, ResponsiveContainer, RadialBarChart, RadialBar, PieChart, Pie, Cell,
} from 'recharts';
import { useState } from 'react';
import ReceiptSnap from '../client-pwa/ReceiptSnap';

const healthHistory = [
  { m: 'Nov', score: 64 }, { m: 'Dec', score: 68 }, { m: 'Jan', score: 71 },
  { m: 'Feb', score: 74 }, { m: 'Mar', score: 78 }, { m: 'Apr', score: 82 },
];

const cashflowGauge = [{ name: 'Cash', value: 72, fill: 'url(#gaugeGradient)' }];

const revenueMix = [
  { name: 'Residential', value: 142000, color: '#a78bfa' },
  { name: 'Commercial', value: 88000, color: '#f0abfc' },
  { name: 'Repairs', value: 38500, color: '#fcd34d' },
  { name: 'Other', value: 16000, color: '#67e8f9' },
];

export default function HomePageV2() {
  const [receiptOpen, setReceiptOpen] = useState(false);

  return (
    <div className="space-y-3 pt-4">
      {/* Editorial date strip */}
      <div className="flex items-center justify-between px-1">
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-white/40">Monday · 28 April</p>
        <p className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          <span className="h-1 w-1 rounded-full bg-emerald-400" />
          Live
        </p>
      </div>

      {/* Hero number — editorial display */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-6 backdrop-blur-xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-white/50">YTD Revenue</p>
            <p className="mt-2 bg-gradient-to-br from-white via-white to-white/60 bg-clip-text text-5xl font-black leading-none tracking-tighter text-transparent">
              $284,500
            </p>
            <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
              <ArrowUpRight className="h-3 w-3" />
              +12.4% YoY
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-white/40">Net profit</p>
            <p className="mt-1 text-2xl font-bold tracking-tight">$48.2k</p>
            <p className="text-[10px] text-emerald-300">+8.1%</p>
          </div>
        </div>
        {/* Background sparkline */}
        <div className="-mx-6 -mb-6 mt-3 h-20">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={healthHistory}>
              <defs>
                <linearGradient id="heroArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="score"
                stroke="#c4b5fd"
                strokeWidth={2}
                fill="url(#heroArea)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bento grid: 2-column with mixed sizes */}
      <div className="grid grid-cols-2 gap-3">
        {/* Health gauge */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-violet-500/20 via-white/[0.04] to-transparent p-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">Health</p>
          <div className="relative mt-1 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="65%" outerRadius="100%" data={cashflowGauge} startAngle={210} endAngle={-30}>
                <defs>
                  <linearGradient id="gaugeGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#a78bfa" />
                    <stop offset="100%" stopColor="#f0abfc" />
                  </linearGradient>
                </defs>
                <RadialBar background={{ fill: 'rgba(255,255,255,0.05)' }} dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-black tracking-tighter">82</span>
              <span className="text-[9px] uppercase tracking-wider text-emerald-300">+4 pts</span>
            </div>
          </div>
        </div>

        {/* Revenue mix donut */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-fuchsia-500/15 via-white/[0.04] to-transparent p-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">Mix</p>
          <div className="relative mt-1 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={revenueMix} dataKey="value" innerRadius={28} outerRadius={42} paddingAngle={2} stroke="none">
                  {revenueMix.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-base font-bold">4</span>
              <span className="text-[8px] uppercase tracking-wider text-white/50">streams</span>
            </div>
          </div>
        </div>

        {/* Briefing — full width */}
        <div className="col-span-2 relative overflow-hidden rounded-3xl border border-violet-400/30 bg-gradient-to-br from-violet-600/20 via-fuchsia-500/10 to-transparent p-5">
          <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-200 backdrop-blur-sm">
            <Sparkles className="h-2.5 w-2.5" />
            AI Brief
          </div>
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-violet-200/80">Weekly Pulse</p>
          <p className="mt-2 text-sm leading-relaxed text-white/90">
            Your <span className="font-bold text-white">strongest week</span> this quarter — revenue $32.4k, up 14%. 3 invoices paid promptly.
          </p>
          <div className="mt-3 flex items-start gap-2 rounded-2xl border border-amber-300/30 bg-amber-400/10 p-3">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-300" />
            <p className="text-[11px] leading-relaxed text-white/90">
              <span className="font-semibold text-amber-200">Acme Builders</span> · $4,200 overdue 12d. Send reminder?
            </p>
          </div>
        </div>

        {/* Snap receipt */}
        <button
          onClick={() => setReceiptOpen(true)}
          className="group relative col-span-2 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-cyan-500/20 via-violet-500/10 to-transparent p-5 text-left transition-all hover:from-cyan-500/30"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 shadow-lg shadow-cyan-500/30">
              <Camera className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold tracking-tight">Snap a receipt</p>
              <p className="text-[11px] text-white/60">AI extracts vendor · amount · GST in 1.4s</p>
            </div>
            <ArrowRight className="h-4 w-4 text-white/40 transition-transform group-hover:translate-x-1" />
          </div>
        </button>

        {/* Deadline */}
        <div className="relative col-span-2 overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-white/[0.04] to-transparent p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-400 text-[#2a2a3a]">
              <span className="text-base font-black">12</span>
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-amber-200">Days until BAS</p>
              <p className="text-sm font-semibold">Q3 BAS lodgement · 28 Apr</p>
            </div>
            <ArrowRight className="h-4 w-4 text-amber-200/60" />
          </div>
        </div>
      </div>

      {/* Latest message — magazine row */}
      <Link
        to="/client-pwa-v2/help"
        className="group relative flex items-center gap-4 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-white/[0.04] to-transparent p-4 backdrop-blur-md"
      >
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 text-sm font-bold text-[#2a2a3a]">
          MB
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold">Marcus Bell</p>
            <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-400" />
            <p className="text-[10px] uppercase tracking-wider text-white/40">2h ago</p>
          </div>
          <p className="mt-0.5 truncate text-xs text-white/60">
            Reviewed your March P&L — looks good. Quick question about the equipment…
          </p>
        </div>
        <ArrowRight className="h-4 w-4 flex-shrink-0 text-white/30 transition-transform group-hover:translate-x-1" />
      </Link>

      <ReceiptSnap open={receiptOpen} onClose={() => setReceiptOpen(false)} />
    </div>
  );
}
